import {
  HELPER_DEFAULT_PORT,
  STUDIO_ORIGIN,
  type InfoResponse,
} from "@makemonlab/helper-client";

/**
 * Web-app side wrapper for talking to the local Helper EXE.
 * All calls are best-effort: if the Helper is not installed/running,
 * helperPing() returns false and helperCall() throws so callers can
 * fall back to cloud / in-browser processing.
 */

const BASE = `http://localhost:${HELPER_DEFAULT_PORT}`;

export async function helperPing(): Promise<boolean> {
  try {
    const r = await fetch(`${BASE}/info`, {
      headers: { Origin: STUDIO_ORIGIN },
      method: "GET",
      cache: "no-store",
    });
    return r.ok;
  } catch {
    return false;
  }
}

export async function helperInfo(): Promise<InfoResponse | null> {
  try {
    const r = await fetch(`${BASE}/info`, {
      headers: { Origin: STUDIO_ORIGIN },
      method: "GET",
      cache: "no-store",
    });
    if (!r.ok) return null;
    return (await r.json()) as InfoResponse;
  } catch {
    return null;
  }
}

export async function helperCall<T = unknown>(endpoint: string, body: unknown): Promise<T> {
  const r = await fetch(`${BASE}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Origin: STUDIO_ORIGIN },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`Helper ${endpoint}: ${r.status}`);
  return r.json() as Promise<T>;
}

/** Chunked ArrayBuffer -> base64 (avoids call-stack overflow on large buffers). */
function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Save an exported video buffer to the user's first Helper work folder
 * under an `exports/` subdirectory. Returns the absolute saved path, or
 * null if the Helper is unavailable / the write failed (caller should
 * then fall back to a browser download).
 */
export async function helperSaveExport(
  filename: string,
  buffer: ArrayBuffer,
): Promise<string | null> {
  try {
    const info = await helperInfo();
    const workFolder = info?.storage?.[0]?.path;
    if (!workFolder) return null;

    const sep = workFolder.includes("\\") ? "\\" : "/";
    const targetPath = `${workFolder}${sep}exports${sep}${filename}`;

    const res = await fetch(`${BASE}/files/write`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Origin: STUDIO_ORIGIN },
      body: JSON.stringify({
        path: targetPath,
        content_base64: arrayBufferToBase64(buffer),
        mode: "overwrite",
      }),
    });
    if (!res.ok) return null;
    return targetPath;
  } catch {
    return null;
  }
}

/**
 * Download a video's audio via the local Helper's bundled yt-dlp.
 *
 * Phase 25-AE — the Helper runs on the user's residential IP, so YouTube
 * does not bot-block it (a datacenter-IP server gets blocked). Returns the
 * raw audio bytes + filename, or null if the Helper is unavailable / the
 * download failed (caller should surface the error).
 */
export async function helperDownloadAudio(
  url: string,
): Promise<{ bytes: ArrayBuffer; filename: string } | null> {
  return helperDownloadMedia(url, "audio");
}

/**
 * Download a video's audio OR video via the local Helper's bundled yt-dlp.
 * kind="video" pulls a progressive mp4 (video+audio in one file) — used by
 * the editor bridge so the analyzed clips have a real source video.
 */
export async function helperDownloadMedia(
  url: string,
  kind: "audio" | "video",
): Promise<{ bytes: ArrayBuffer; filename: string } | null> {
  try {
    const r = await fetch(`${BASE}/ytdlp/download`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Origin: STUDIO_ORIGIN },
      body: JSON.stringify({ url, kind }),
    });
    if (!r.ok) {
      const detail = await r.text().catch(() => "");
      console.error("[helper] ytdlp/download failed", r.status, detail.slice(0, 300));
      return null;
    }
    const filename = r.headers.get("X-Audio-Filename") || "audio.m4a";
    const bytes = await r.arrayBuffer();
    console.log("[helper] ytdlp/download ok", { filename, bytes: bytes.byteLength });
    return { bytes, filename };
  } catch (e) {
    console.error("[helper] ytdlp/download error", e);
    return null;
  }
}

// Whisper transcription is server-side (OpenAI Whisper API via Workers Edge) —
// see apps/web/src/lib/transcribe.ts. The Helper is not involved in
// transcription; it only handles local file I/O for video export.
